// Schemas API
import type { PendingSchemaEdit } from '@/features/schema/fork'
import { apiClient } from './client'

export interface PropertySchema {
  name: string
  type: string
  description?: string
  required?: boolean
  unique?: boolean
  // Marks this property's value as the entity's display name — shown wherever the
  // entity is rendered (graph node label, search result, breadcrumb, tooltip).
  // At most one per entity type; zero falls back to a deterministic name chain.
  is_display_name?: boolean
}

/**
 * How duplicate entities with unique properties are merged: ANY or ALL unique
 * properties must match. Constrained to the two values the resolution engine
 * honors — free-text named strategies are not supported (ADR-0002).
 */
export type MergeStrategy = 'any' | 'all'

export interface EntityType {
  id: string
  label: string
  description?: string
  properties: PropertySchema[]
  examples?: string[]
  // null on legacy rows that predate the merge-strategy column.
  merge_strategy?: MergeStrategy | null
  // Convention overrides — see /docs/schema-conventions
  trackable?: boolean
  timeline_exclude?: string[]
  display_label?: Record<string, string>
}

export interface RelationType {
  id: string
  label: string
  description?: string
  properties: PropertySchema[]
  source_types?: string[]
  target_types?: string[]
  examples?: string[]
  // Convention overrides — see /docs/schema-conventions
  timeline_exclude?: string[]
}

export interface ExtractionPattern {
  id: string
  source_type: string
  relationship_type: string
  target_type: string
  description?: string
}

export interface Schema {
  id: string
  name: string
  description?: string
  version: number
  is_active: boolean
  // Ownership marker (ADR-0003): null/absent ⇒ global, set ⇒ private to a case.
  case_id?: string | null
  // Draft marker (ADR-0028): set ⇒ a schema-agent session's draft, outside every lineage.
  draft_session_id?: string | null
  additional_node_types: boolean
  additional_relationship_types: boolean
  additional_patterns: boolean
  additional_properties_allowed: boolean
  created_at?: string
  updated_at?: string
  // Detail rows (GET /schemas/:id) carry the full type arrays. List rows
  // (GET /schemas) omit them and carry the *_count fields below instead.
  entity_types: EntityType[]
  relationship_types: RelationType[]
  patterns: ExtractionPattern[]
  // Present on list rows; absent on detail rows (derive from the arrays there).
  entity_count?: number
  relation_count?: number
  pattern_count?: number
}

export interface CreateEntityTypeRequest {
  label: string
  description: string
  properties: PropertySchema[]
  merge_strategy?: MergeStrategy
  trackable?: boolean
  timeline_exclude?: string[]
  display_label?: Record<string, string>
}

export interface UpdateEntityTypeRequest {
  label: string
  description: string
  properties: PropertySchema[]
  merge_strategy?: MergeStrategy
  trackable?: boolean
  timeline_exclude?: string[]
  display_label?: Record<string, string>
}

export interface CreateRelationTypeRequest {
  label: string
  description: string
  properties: PropertySchema[]
  timeline_exclude?: string[]
}

export interface UpdateRelationTypeRequest {
  label: string
  description: string
  properties: PropertySchema[]
  timeline_exclude?: string[]
}

export interface CreatePatternRequest {
  source_type: string
  relationship_type: string
  target_type: string
  description?: string
}

export interface UpdatePatternRequest {
  source_type: string
  relationship_type: string
  target_type: string
  description?: string
}

export interface ImportSchemaRequest {
  name: string
  description?: string
  is_active?: boolean
  additional_node_types?: boolean
  additional_relationship_types?: boolean
  additional_patterns?: boolean
  additional_properties_allowed?: boolean
  entity_types?: unknown[]
  relationship_types?: unknown[]
  patterns?: unknown[]
}

// ---------------------------------------------------------------------------
// Schema convention preview (v1)
// ---------------------------------------------------------------------------

export type SchemaPreviewMapRenderMode =
  | 'animated_track'
  | 'time_anchored_pin'
  | 'static_pin'
  | null

export interface SchemaPreviewAspect {
  kind: 'point' | 'interval'
  properties: string[]
  label: string
}

export interface SchemaPreviewWaypointSource {
  relation: string
  time_property: string
  target: string
  target_geo_mode: 'inline' | 'via_edge' | 'none'
}

export interface SchemaPreviewWarning {
  code: string
  message: string
}

export interface SchemaPreview {
  label: string
  source: 'entity' | 'relation'
  temporal_aspects: SchemaPreviewAspect[]
  geo: { mode: 'none' | 'inline' | 'via_edge'; via?: string | null }
  trackable: boolean
  map_render_mode: SchemaPreviewMapRenderMode
  trajectory_preview: SchemaPreviewWaypointSource[] | null
  warnings: SchemaPreviewWarning[]
}

// Schema-concept registry — see openspec/changes/schema-concepts-registry/.
export type ConceptSeverity = 'error' | 'warning' | 'hint' | 'meta'

export type ConceptAttachment = 'entity' | 'property' | 'relationship' | 'schema'

export interface ConceptEffect {
  view: string[]
  outcome: string
}

export interface ConceptDefinition {
  title: string
  summary: string
  severity: ConceptSeverity
  attachment: ConceptAttachment
  domain: string
  effect: ConceptEffect
  vocab: Record<string, unknown>
  requires: string[]
}

export interface ConceptsRegistry {
  version: number
  concepts: Record<string, ConceptDefinition>
}

export type ValidationGate = 'save_draft' | 'activate_for_extraction'

export interface ValidationFinding {
  concept_id: string
  severity: ConceptSeverity
  location: string
  message: string
}

export interface ValidateSchemaResponse {
  findings: ValidationFinding[]
  gate: ValidationGate | null
}

export const schemasApi = {
  // Global schema list (admin page).
  list: () => apiClient.get<Schema[]>('/v1/schemas'),

  // Case-view list: the global schemas plus this case's own private schemas.
  // Membership-gated on the backend (`/cases/{caseId}/schemas`).
  listForCase: (caseId: string) =>
    apiClient.get<Schema[]>(`/v1/cases/${encodeURIComponent(caseId)}/schemas`),

  // Create a case-owned schema (Person-seeded) and auto-bind the case to it.
  // Collaborator-gated; returns the new schema's full detail.
  createForCase: (caseId: string, request: { name: string }) =>
    apiClient.post<Schema>(`/v1/cases/${encodeURIComponent(caseId)}/schemas`, request),

  // Fork a (global) schema into a case-owned copy and auto-rebind the case.
  // `edit` is optional — omit it for a pure copy (the "start from a global
  // schema" intro path), or pass the triggering edit for fork-on-edit. Returns
  // the fork's full detail.
  fork: (
    caseId: string,
    schemaId: string,
    request: { name?: string | null; edit?: PendingSchemaEdit }
  ) =>
    apiClient.post<Schema>(
      `/v1/cases/${encodeURIComponent(caseId)}/schemas/${encodeURIComponent(schemaId)}/fork`,
      request
    ),

  // Re-pick the case's starting point: create the replacement (a copy of
  // `source_schema_id`, or a blank Person-seeded schema when it's null), rebind
  // the case, and DELETE every schema it owned before. Destructive and
  // collaborator-gated; returns the replacement's full detail.
  replaceForCase: (caseId: string, request: { source_schema_id: string | null; name?: string }) =>
    apiClient.post<Schema>(`/v1/cases/${encodeURIComponent(caseId)}/schemas/replace`, request),

  // Import a JSON schema as a case-owned schema (hidden globally). Collaborator-
  // gated; mirrors `importFromJson` but threads the case ownership server-side.
  importForCase: (caseId: string, payload: ImportSchemaRequest) =>
    apiClient.post<Schema>(`/v1/cases/${encodeURIComponent(caseId)}/schemas/import`, payload),

  // New version of a case-visible schema; carries the source's ownership forward
  // (a case schema stays case-owned). Collaborator-gated. The case editor sends
  // one change per call — an entity/relation/pattern `edit` or a schema-level
  // `description` — so every modification lands on a fresh immutable version
  // (SP-385 auto-versioning); an empty body is a plain version bump.
  createVersionForCase: (
    caseId: string,
    schemaId: string,
    body?: { edit?: PendingSchemaEdit; description?: string }
  ) =>
    apiClient.post<Schema>(
      `/v1/cases/${encodeURIComponent(caseId)}/schemas/${encodeURIComponent(schemaId)}/versions`,
      body ?? {}
    ),

  // Convention preview for an entity or relation type. Powered by the v1
  // schema preview endpoint; gated by the TIMELINE_PROJECTOR_V2 feature
  // flag on the backend (404 when disabled).
  getTypePreview: (schemaId: string, label: string, source: 'entity' | 'relation' = 'entity') =>
    apiClient.get<SchemaPreview>(
      `/v1/schemas/${schemaId}/types/${encodeURIComponent(label)}/preview?source=${source}`
    ),

  get: (schemaId: string) => apiClient.get<Schema>(`/v1/schemas/${schemaId}`),

  getActive: () => apiClient.get<Schema>('/v1/schemas/active'),

  activate: (schemaId: string) => apiClient.post<void>(`/v1/schemas/${schemaId}/activate`),

  delete: (schemaId: string) => apiClient.delete<void>(`/v1/schemas/${schemaId}`),

  createVersion: (schemaId: string) =>
    apiClient.post<Schema>(`/v1/schemas/${schemaId}/versions`, {}),

  importFromJson: (payload: ImportSchemaRequest) =>
    apiClient.post<Schema>('/v1/schemas/import', payload),

  // Entity types
  createEntityType: (schemaId: string, request: CreateEntityTypeRequest) =>
    apiClient.post<EntityType>(`/v1/schemas/${schemaId}/entities`, request),

  updateEntityType: (entityId: string, request: UpdateEntityTypeRequest) =>
    apiClient.put<EntityType>(`/v1/schemas/entities/${entityId}`, request),

  deleteEntityType: (entityId: string) =>
    apiClient.delete<void>(`/v1/schemas/entities/${entityId}`),

  // Relation types
  createRelationType: (schemaId: string, request: CreateRelationTypeRequest) =>
    apiClient.post<RelationType>(`/v1/schemas/${schemaId}/relations`, request),

  updateRelationType: (relationId: string, request: UpdateRelationTypeRequest) =>
    apiClient.put<RelationType>(`/v1/schemas/relations/${relationId}`, request),

  deleteRelationType: (relationId: string) =>
    apiClient.delete<void>(`/v1/schemas/relations/${relationId}`),

  // Extraction patterns
  createPattern: (schemaId: string, request: CreatePatternRequest) =>
    apiClient.post<ExtractionPattern>(`/v1/schemas/${schemaId}/patterns`, request),

  updatePattern: (patternId: string, request: UpdatePatternRequest) =>
    apiClient.put<ExtractionPattern>(`/v1/schemas/patterns/${patternId}`, request),

  deletePattern: (patternId: string) => apiClient.delete<void>(`/v1/schemas/patterns/${patternId}`),

  // Schema-concept registry — used by the schema playground.
  getConcepts: () => apiClient.get<ConceptsRegistry>('/v1/schemas/concepts'),

  validate: (schema: Record<string, unknown>, gate?: ValidationGate) =>
    apiClient.post<ValidateSchemaResponse>('/v1/schemas/validate', { schema, gate: gate ?? null }),
}
